/**
 * Settings popup (PRD "Settings popup", §5.1 #20-#21 + #35-#38, §4.1).
 *
 * Sections behind quiet text tabs, because they save differently:
 *  - **General** keeps a local draft and sends one partial `PUT /api/settings` on Save, so
 *    untouched keys are never rewritten and the server's side effects (reschedule on tz/hour) fire
 *    only when they should — `countedPostTypes` is edited from the Metrics header instead;
 *  - **Scrapers** owns per-plugin config and the active bindings, each saving on its own action —
 *    a connection test has to run against what is actually stored, not against a pending draft;
 *  - **Watching** (#68-#76) owns its own blob and its own Save, and is the only tab whose settings
 *    the shared `PUT /api/settings` never touches;
 *  - **Comment Generation** (#77-#81) mixes both — per-action saves for the backend block, one
 *    draft and one Save for the prompt pair.
 *
 * The logout action lives at the bottom of this popup (redesign § Homescreen — the top bar
 * is gone).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { SettingsResponse, SettingsUpdateBody } from '@lw/shared';
import { getStatus } from '../../api/status';
import { logout } from '../../api/auth';
import { QK } from '../../api/queryKeys';
import { describeError } from '../../api/client';
import { LOGIN_PATH } from '../../app/routes';
import { ErrorState, Modal, Spinner, cx } from '../../components';
import { formatBytes } from '../../lib/format';
import { browserTimeZone } from '../../lib/datetime';
import { JOB_STATUS_LABEL, JOB_TYPE_LABEL } from '../../lib/labels';
import { AiSection } from './AiSection';
import { NotificationsSection } from './NotificationsSection';
import { NotionSection } from './NotionSection';
import { ScrapersSection } from './ScrapersSection';
import { TelegramSection } from './TelegramSection';
import { WatchingSection } from './WatchingSection';
import { timezoneOptions } from './timezones';
import { useSettingsQuery, useUpdateSettings } from './useSettings';
import './settings.css';

type Draft = {
  backfillMonths: string;
  rescrapeDays: string;
  scrapeHour: string;
  timezone: string;
  mediaEnabled: boolean;
};

export type SettingsTab =
  | 'general'
  | 'scrapers'
  | 'notion'
  | 'notifications'
  | 'telegram'
  | 'watching'
  | 'ai';
type Tab = SettingsTab;

/** Key order is tab order. */
const TAB_LABEL: Record<Tab, string> = {
  general: 'General',
  scrapers: 'Scrapers',
  notion: 'Notion',
  notifications: 'Notifications',
  telegram: 'Telegram',
  watching: 'Watching',
  ai: 'Comment Generation',
};

function toDraft(settings: SettingsResponse): Draft {
  return {
    backfillMonths: String(settings.backfillMonths),
    rescrapeDays: String(settings.rescrapeDays),
    scrapeHour: String(settings.scrapeHour),
    timezone: settings.timezone ?? browserTimeZone(),
    mediaEnabled: settings.mediaEnabled,
  };
}

type NumericField = { key: 'backfillMonths' | 'rescrapeDays' | 'scrapeHour'; min: number; max: number };

const NUMERIC_FIELDS: NumericField[] = [
  { key: 'backfillMonths', min: 1, max: 120 },
  { key: 'rescrapeDays', min: 1, max: 3650 },
  { key: 'scrapeHour', min: 0, max: 23 },
];

function numericErrors(draft: Draft): Partial<Record<NumericField['key'], string>> {
  const errors: Partial<Record<NumericField['key'], string>> = {};
  for (const field of NUMERIC_FIELDS) {
    const raw = draft[field.key];
    const parsed = Number(raw);
    if (raw.trim().length === 0 || !Number.isInteger(parsed) || parsed < field.min || parsed > field.max) {
      errors[field.key] = `Enter a whole number between ${field.min} and ${field.max}.`;
    }
  }
  return errors;
}

export type SettingsModalProps = {
  open: boolean;
  onClose: () => void;
  /** Opens straight on a tab — the "configure in Settings" toast points here. */
  initialTab?: Tab;
};

export function SettingsModal({ open, onClose, initialTab = 'general' }: SettingsModalProps) {
  const settingsQuery = useSettingsQuery();
  const settings = settingsQuery.data;
  const update = useUpdateSettings();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>(initialTab);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);

  const signOut = useMutation({
    mutationFn: () => logout(),
    onSettled: () => {
      // Drop every cached answer: the next screen must not paint another session's data.
      queryClient.clear();
      void navigate(LOGIN_PATH, { replace: true });
    },
  });

  // Re-seed the draft whenever the modal opens or the server value changes underneath it.
  useEffect(() => {
    if (!open) {
      setDraft(null);
      setSaved(false);
      return;
    }
    setTab(initialTab);
    if (settings !== undefined) setDraft((current) => current ?? toDraft(settings));
  }, [open, settings, initialTab]);

  // Job progress: the Scrapers tab triggers re-map jobs (#33/#34), and a counted-post-types change
  // made elsewhere enqueues recompute_baselines. The same read carries the unmappable-payload
  // notice the Scrapers section shows (#18).
  const statusQuery = useQuery({
    queryKey: QK.status,
    queryFn: ({ signal }) => getStatus(signal),
    enabled: open,
    refetchInterval: open ? 4000 : false,
  });
  const activeJob = statusQuery.data?.activeJob ?? null;

  const timezones = useMemo(
    () => timezoneOptions(settings?.timezone ?? null, browserTimeZone()),
    [settings?.timezone],
  );

  const errors = draft === null ? {} : numericErrors(draft);
  const hasErrors = Object.keys(errors).length > 0;

  const body: SettingsUpdateBody | null = useMemo(() => {
    if (draft === null || settings === undefined || hasErrors) return null;
    const next: SettingsUpdateBody = {};
    if (Number(draft.backfillMonths) !== settings.backfillMonths) {
      next.backfillMonths = Number(draft.backfillMonths);
    }
    if (Number(draft.rescrapeDays) !== settings.rescrapeDays) {
      next.rescrapeDays = Number(draft.rescrapeDays);
    }
    if (Number(draft.scrapeHour) !== settings.scrapeHour) next.scrapeHour = Number(draft.scrapeHour);
    if (draft.timezone !== (settings.timezone ?? '')) next.timezone = draft.timezone;
    if (draft.mediaEnabled !== settings.mediaEnabled) next.mediaEnabled = draft.mediaEnabled;
    return Object.keys(next).length > 0 ? next : null;
  }, [draft, settings, hasErrors]);

  const patch = (changes: Partial<Draft>) => {
    setSaved(false);
    setDraft((current) => (current === null ? current : { ...current, ...changes }));
  };

  const save = () => {
    if (body === null) return;
    update.mutate(body, {
      onSuccess: (next) => {
        setDraft(toDraft(next));
        setSaved(true);
        void statusQuery.refetch();
      },
    });
  };

  return (
    <Modal
      open={open}
      title="Settings"
      size="lg"
      onClose={onClose}
      footer={
        <>
          {saved && body === null ? <span className="muted dialog-hint">Saved.</span> : null}
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Close</span>
          </button>
          {tab === 'general' ? (
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={body === null || update.isPending}
            >
              {update.isPending ? <Spinner size="sm" /> : null}
              Save changes
            </button>
          ) : null}
        </>
      }
    >
      <div className="settings">
        <div className="settings-tabs" role="tablist">
          {(Object.keys(TAB_LABEL) as Tab[]).map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              className={cx('settings-tab', tab === name && 'settings-tab--on')}
              onClick={() => setTab(name)}
            >
              {TAB_LABEL[name]}
            </button>
          ))}
        </div>

        {settingsQuery.isPending ? <Spinner size="lg" label="Loading settings…" /> : null}

        {settingsQuery.isError ? (
          <ErrorState
            title="Couldn't load settings"
            error={settingsQuery.error}
            onRetry={() => void settingsQuery.refetch()}
            retrying={settingsQuery.isFetching}
          />
        ) : null}

        {settings !== undefined && draft !== null ? (
          <div className="settings-sections">
            {update.isError ? (
              <p className="form-error" role="alert">
                {describeError(update.error)}
              </p>
            ) : null}

            {activeJob !== null ? (
              <div className="settings-job" role="status">
                <div className="row">
                  <Spinner size="sm" />
                  <strong>{JOB_TYPE_LABEL[activeJob.type]}</strong>
                  <span className="muted">{JOB_STATUS_LABEL[activeJob.status]}</span>
                </div>
                <progress
                  className="settings-job__bar"
                  value={activeJob.progress.done}
                  max={Math.max(activeJob.progress.total, 1)}
                />
                <span className="faint">
                  {activeJob.progress.done} / {activeJob.progress.total}
                </span>
              </div>
            ) : null}

            {tab === 'ai' ? (
              <AiSection />
            ) : tab === 'watching' ? (
              <WatchingSection />
            ) : tab === 'telegram' ? (
              <TelegramSection />
            ) : tab === 'notifications' ? (
              <NotificationsSection settings={settings} />
            ) : tab === 'notion' ? (
              <NotionSection />
            ) : tab === 'scrapers' ? (
              <ScrapersSection
                unmappable={statusQuery.data?.unmappablePayloads ?? null}
                unmappedFields={statusQuery.data?.unmappedFields ?? []}
              />
            ) : (
              <>
                {/* ------------------------------------------------------ scheduling */}
                <section className="settings-section">
                  <h3 className="settings-title">Scraping windows</h3>
                  <div className="settings-grid">
                    <div className="field">
                      <label className="field__label" htmlFor="settings-backfill">
                        Backfill depth (months)
                      </label>
                      <input
                        id="settings-backfill"
                        className="input"
                        type="number"
                        min={1}
                        max={120}
                        value={draft.backfillMonths}
                        onChange={(event) => patch({ backfillMonths: event.target.value })}
                      />
                      {errors.backfillMonths ? (
                        <span className="settings-bad">{errors.backfillMonths}</span>
                      ) : (
                        <span className="field__hint">
                          How far back the first successful scrape reaches.
                        </span>
                      )}
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="settings-rescrape">
                        Re-scrape window (days)
                      </label>
                      <input
                        id="settings-rescrape"
                        className="input"
                        type="number"
                        min={1}
                        max={3650}
                        value={draft.rescrapeDays}
                        onChange={(event) => patch({ rescrapeDays: event.target.value })}
                      />
                      {errors.rescrapeDays ? (
                        <span className="settings-bad">{errors.rescrapeDays}</span>
                      ) : (
                        <span className="field__hint">
                          Posts newer than this are re-fetched for metric growth.
                        </span>
                      )}
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="settings-hour">
                        Scrape hour (0-23)
                      </label>
                      <input
                        id="settings-hour"
                        className="input"
                        type="number"
                        min={0}
                        max={23}
                        value={draft.scrapeHour}
                        onChange={(event) => patch({ scrapeHour: event.target.value })}
                      />
                      {errors.scrapeHour ? (
                        <span className="settings-bad">{errors.scrapeHour}</span>
                      ) : (
                        <span className="field__hint">
                          Daily/Mon–Fri/weekly/monthly runs fire at this hour, app time.
                        </span>
                      )}
                    </div>
                    <div className="field">
                      <label className="field__label" htmlFor="settings-tz">
                        Timezone
                      </label>
                      <select
                        id="settings-tz"
                        className="select"
                        value={draft.timezone}
                        onChange={(event) => patch({ timezone: event.target.value })}
                      >
                        {timezones.map((zone) => (
                          <option key={zone} value={zone}>
                            {zone}
                            {zone === browserTimeZone() ? ' (browser)' : ''}
                          </option>
                        ))}
                      </select>
                      <span className="field__hint">
                        Used for every displayed time and all time-based analytics.
                      </span>
                    </div>
                  </div>
                </section>

                {/* ---------------------------------------------------------- media */}
                <section className="settings-section">
                  <h3 className="settings-title">Media</h3>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={draft.mediaEnabled}
                      onChange={(event) => patch({ mediaEnabled: event.target.checked })}
                    />
                    <span>Load media automatically</span>
                  </label>
                  <p className="field__hint">
                    Off by default. Images and video always load through the server proxy; with this
                    off, each block shows a placeholder with a per-post "Load media" action.
                  </p>
                </section>

                {/* ------------------------------------------------------- read-only */}
                <section className="settings-section">
                  <h3 className="settings-title">Storage</h3>
                  <dl className="settings-readonly">
                    <div>
                      <dt>Database</dt>
                      <dd>{formatBytes(settings.readOnly.dbSizeBytes)}</dd>
                    </div>
                    <div>
                      <dt>Raw payloads</dt>
                      <dd>{formatBytes(settings.readOnly.rawStoreBytes)}</dd>
                    </div>
                    <div>
                      <dt>Media cache</dt>
                      <dd>{formatBytes(settings.readOnly.mediaCacheBytes)}</dd>
                    </div>
                    <div>
                      <dt>App version</dt>
                      <dd>{settings.readOnly.appVersion}</dd>
                    </div>
                  </dl>
                  <p className="muted">
                    The app password is set through the environment, not here (v1).
                  </p>
                </section>
              </>
            )}
          </div>
        ) : null}

        <div className="settings-logout">
          <button
            type="button"
            className="table-action"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            {signOut.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Log out</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default SettingsModal;
